import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import RemoveIcon from '@mui/icons-material/Remove'
import type { InputHTMLAttributes } from 'react'
import type { RoleGroup } from './roles'
import { NumberedSectionCard } from '~/shared/ui'

interface NewTeamSectionProps {
  roles: RoleGroup[]
  // Departing provider per role; a role can only be swapped (given a replacement) when it has one.
  currentByRole: Record<string, string>
  newByRole: Record<string, string>
  onNewChange: (roleName: string, value: string) => void
  checkByRole: Record<string, boolean>
  onCheckChange: (roleName: string, checked: boolean) => void
  onValidate: () => void
  canValidate: boolean
  isValidating: boolean
  locked: boolean
  collapsed: boolean
  onToggleCollapsed: () => void
}

/**
 * A replacement picker per role (leave "No change" for roles that stay). Each clinical role is
 * license-checked against every member's state by default; unchecking "Include in license check"
 * intentionally assigns a provider with known license gaps. The backend computes the licensure.
 */
const NewTeamSection = ({
  roles,
  currentByRole,
  newByRole,
  onNewChange,
  checkByRole,
  onCheckChange,
  onValidate,
  canValidate,
  isValidating,
  locked,
  collapsed,
  onToggleCollapsed,
}: NewTeamSectionProps) => {
  const replacements = roles
    .filter(role => newByRole[role.name])
    .map(role => {
      const provider = role.providers.find(p => String(p.id) === newByRole[role.name])
      return `${role.abbr} → ${provider?.full_name ?? ''}`.trim()
    })

  const summary = (
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
      {replacements.length ? replacements.join(', ') : 'No replacements selected'}
    </Typography>
  )

  return (
    <NumberedSectionCard
      index={2}
      title="New care team"
      locked={locked}
      hint={
        <>
          Locked until you run a search. Choose replacements only for the roles that are changing.
          Each clinical role is checked against every member&apos;s state by default; uncheck{' '}
          <b>Include in license check</b> to intentionally assign a provider with known license
          gaps.
        </>
      }
      collapsible
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      summary={summary}
      testId="new-team-card"
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        }}
      >
        {roles.map(role => {
          // Only a role with a departing provider can be swapped; the rest are inert, so disable them.
          const swappable = Boolean(currentByRole[role.name])
          return (
            <Box key={role.name}>
              <TextField
                select
                fullWidth
                disabled={!swappable}
                label={`${role.name} (${role.abbr})`}
                value={newByRole[role.name] ?? ''}
                onChange={event => onNewChange(role.name, event.target.value)}
                helperText={swappable ? undefined : 'Select a departing provider first'}
                data-testid={`new-${role.name}`}
              >
                <MenuItem value="">— No change</MenuItem>
                {role.providers.map(provider => (
                  <MenuItem key={provider.id} value={String(provider.id)}>
                    {provider.full_name}
                  </MenuItem>
                ))}
              </TextField>
              {role.clinical ? (
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      disabled={!swappable}
                      checked={checkByRole[role.name] ?? true}
                      onChange={(_event, checked) => onCheckChange(role.name, checked)}
                      slotProps={{
                        input: {
                          'data-testid': `license-check-${role.name}`,
                        } as InputHTMLAttributes<HTMLInputElement>,
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 12 }}>Include in license check</Typography>}
                />
              ) : (
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: 11,
                    color: 'text.disabled',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <RemoveIcon sx={{ fontSize: 14 }} /> No license required
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={onValidate}
          disabled={!canValidate || isValidating}
          data-testid="validate-button"
        >
          Validate licensure
        </Button>
        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
          {canValidate
            ? 'Ready to check the proposed team against member states.'
            : 'Select at least one replacement provider.'}
        </Typography>
      </Box>
    </NumberedSectionCard>
  )
}

export default NewTeamSection
